import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: "/", label: "Accueil" },
    { href: "/process", label: "Process" },
    { href: "/services", label: "Prestations" },
    { href: "/vehicules-disponibles", label: "Véhicules" },
    { href: "/galerie", label: "Galerie" },
    { href: "/simulateur", label: "Simulateur" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Banner - fastandcar.com */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black">
        <div className="max-w-7xl mx-auto px-2 md:px-0">
          <img
            src="/assets/banner-1.png"
            alt="Fast&Car - Spécialiste de l'importation"
            className="w-full h-auto max-h-16 md:max-h-24 object-contain"
            data-testid="site-banner"
          />
        </div>
      </div>
      
      <header
        data-testid="header"
        className={`fixed top-16 md:top-24 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled
            ? "glass border-b border-zinc-800"
            : "bg-black/90 backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <span className="font-unbounded text-lg md:text-xl font-black text-white">
                FAST<span className="text-neon-lime">&</span>CAR
              </span>
              <span className="hidden md:inline text-zinc-500 text-xs ml-2 font-mono">.com</span>
            </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`font-manrope text-sm font-medium uppercase tracking-widest transition-colors ${
                  isActive(link.href)
                    ? "text-neon-lime nav-link-active"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:0768013271"
              className="flex items-center gap-2 text-zinc-400 hover:text-neon-lime transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="font-mono text-sm">07 68 01 32 71</span>
            </a>
            <Link to="/contact">
              <Button
                data-testid="header-cta-btn"
                className="bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider px-6 py-2 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
              >
                <span>Devis gratuit</span>
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden bg-black/95 border-t border-zinc-800 py-4 px-4"
          >
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`font-manrope text-base font-medium uppercase tracking-wider py-3 px-4 ${
                    isActive(link.href)
                      ? "text-neon-lime bg-zinc-900"
                      : "text-zinc-400"
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-zinc-800 pt-4 mt-2">
                <a
                  href="tel:0768013271"
                  className="flex items-center justify-center gap-2 text-neon-lime py-3 bg-zinc-900"
                >
                  <Phone className="w-5 h-5" />
                  <span className="font-mono text-lg">07 68 01 32 71</span>
                </a>
                <Link to="/contact" className="block mt-2">
                  <Button className="w-full bg-neon-lime text-black font-unbounded font-bold uppercase py-4">
                    Devis gratuit
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
    </>
  );
};

export default Header;
